// deployment_class: customer_env_allowed
/**
 * The Apple UI modifier — drag in, drag out. No buttons.
 *
 * ── What E3 asked for (2026-08-29) ─────────────────────────────────────────
 * E3: *"What I really want is the exact way that Apple works in terms of
 * dropping and dragging elements. There are no x's and arrows by the existing
 * elements. There's just the importing and exporting of the element."*
 *
 * macOS "Customize Toolbar…" has exactly three gestures and zero buttons:
 *
 *   drag from the palette INTO the toolbar   → add
 *   drag OUT of the toolbar                  → remove (it returns to the palette)
 *   drag WITHIN the toolbar                  → reorder
 *
 * Apple can afford no delete control because removal is not destruction: the
 * palette always holds every element, so taking one out just puts it back where
 * it lives. Our package works the same way, which is what makes E3's *"anything
 * that gets removed can easily be replaced"* true by construction rather than
 * by remembering to build an undo.
 *
 * ── Why keyboard support costs nothing here ────────────────────────────────
 * The `×`/arrow controls existed because `useDraggable` without a `DndContext`
 * ancestor is an invalid hook call that took the whole module down, and the
 * fallback then looked like a decision. It was not one — and the accessibility
 * worry behind it was unfounded: `@dnd-kit` ships a `KeyboardSensor` (space to
 * lift, arrows to move, space to drop) with live-region announcements from
 * `@dnd-kit/accessibility`. Apple's no-buttons model and the WCAG 2.2 AA floor
 * are therefore ONE implementation, not two.
 *
 * ── What works, and what does not, stated exactly ──────────────────────────
 * MODULES: all three gestures. Drag one in the rail onto another to reorder,
 * drag it out to remove it. Permanent modules (Executive · Projects) carry no
 * handle at all.
 *
 * ELEMENTS: add and remove. REORDERING within a module is not here, because
 * elements are still hardcoded across two containers — eyebrow inside the
 * header, tabs outside it — so sorting them means rendering from the ordered
 * list and restructuring the DOM. That is a layout change and it earns its own
 * pass with its own visual verification rather than riding along here.
 *
 * Saying so precisely matters: the previous version of this file claimed the
 * Apple model was in while `DraggableTile` had no importer at all, so dragging
 * did nothing. A comment that overstates what is wired is how that happens.
 */
import {
  DndContext,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { ElementKind } from './template-state';

/**
 * The context every draggable needs an ancestor of.
 *
 * Rendered ALWAYS, not only while editing. `useDraggable` throws an invalid
 * hook call without this ancestor — that is the crash that took the whole
 * module down and produced the button fallback in the first place — and
 * mounting or unmounting a context by mode would remount the subtree under it,
 * losing scroll position and focus every time edit mode is entered.
 *
 * Two sensors, deliberately:
 *   PointerSensor  — a 6px activation distance so a CLICK is still a click.
 *                    Without it every tab press would begin a drag.
 *   KeyboardSensor — space to lift, arrows to move, space to drop, announced
 *                    through @dnd-kit/accessibility. This is the whole reason
 *                    the ×/arrow controls are not needed for the WCAG floor.
 */
export function TemplateDndProvider({
  onDrop,
  children,
}: {
  onDrop: (payload: DragPayload, target: DropTarget) => void;
  children: React.ReactNode;
}): JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  function handleDragEnd(e: DragEndEvent): void {
    const payload = e.active.data.current as DragPayload | undefined;
    if (!payload) return;
    const over = e.over?.id;
    // A drop outside every zone is a real gesture, not a mistake: in Finder,
    // dragging an item off the toolbar removes it. `null` carries that.
    if (typeof over === 'string' && over.startsWith('module-slot:')) {
      onDrop(payload, { kind: 'module-slot', moduleKey: over.slice('module-slot:'.length) });
      return;
    }
    onDrop(payload, over === 'package' ? { kind: 'package' } : { kind: 'elsewhere' });
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      {children}
    </DndContext>
  );
}

/**
 * Where a drag started, so the drop handler knows which gesture it is.
 *
 * A discriminated union rather than optional fields: a module drag has no
 * `kind` and an element drag has no `moduleKey`, and letting both be optional
 * would mean every handler guessing which case it is holding.
 */
/**
 * Where a drag ended.
 *
 * `elsewhere` is a first-class outcome, not a failure: dropping something off
 * the toolbar is how Finder removes it, so "nowhere in particular" has to be
 * expressible rather than swallowed.
 */
export type DropTarget =
  | { readonly kind: 'package' }
  | { readonly kind: 'module-slot'; readonly moduleKey: string }
  | { readonly kind: 'elsewhere' };

export type DragPayload =
  /** From the palette → placing it. */
  | { readonly from: 'package'; readonly kind: ElementKind }
  /** Already in the module → carrying it out. `id` because two spacers look alike. */
  | { readonly from: 'placed'; readonly kind: ElementKind; readonly id: string }
  /** A module in the rail → reordering it, or carrying it out of the workspace. */
  | { readonly from: 'module'; readonly moduleKey: string };

/**
 * A palette tile you can pick up.
 *
 * Still renders a real `<button>` underneath: dnd-kit's keyboard sensor needs a
 * focusable element, and a tile that only responds to a pointer would fail the
 * accessibility floor no matter how good the drag felt.
 */
export function DraggableTile({
  kind,
  disabled,
  onClick,
  children,
  className,
  label,
}: {
  kind: ElementKind;
  disabled: boolean;
  /**
   * Click adds too, and that is not a fallback.
   *
   * The pointer sensor has a 6px activation distance, so a click that does not
   * move never becomes a drag and this fires cleanly. Keeping it means the
   * palette works for someone who does not want to drag at all — which is most
   * people, most of the time.
   */
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
  label: string;
}): JSX.Element {
  const payload: DragPayload = { from: 'package', kind };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `pkg:${kind}`,
    data: payload,
    disabled,
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`${className ?? ''} ${isDragging ? 'dragging' : ''}`.trim()}
      disabled={disabled}
      onClick={onClick}
      aria-label={
        disabled ? `${label} — already in this module` : `Add ${label} to this module — or drag it in`
      }
      {...listeners}
      {...attributes}
    >
      {children}
    </button>
  );
}

/**
 * A placed element you can pick up and carry out of the module.
 *
 * Wraps rather than replaces, so nothing about the element's own markup or
 * class names changes — the layout is untouched and only a drag handle is added
 * around it. Permanent elements are simply not draggable: the title and the
 * module name cannot be carried out, matching the refusal already enforced in
 * the state layer so the gesture cannot become a second route around the rule.
 */
export function DraggablePlaced({
  kind,
  id,
  permanent,
  label,
  children,
}: {
  kind: ElementKind;
  id: string;
  permanent: boolean;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  const payload: DragPayload = { from: 'placed', kind, id };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `placed:${id}`,
    data: payload,
    disabled: permanent,
  });

  if (permanent) return <>{children}</>;

  return (
    <span
      ref={setNodeRef}
      className={`kd-drag ${isDragging ? 'dragging' : ''}`.trim()}
      aria-label={`${label} — drag out to remove`}
      {...listeners}
      {...attributes}
    >
      {children}
    </span>
  );
}

/**
 * A module in the rail — drag it to reorder, drag it out to remove it.
 *
 * ── No arrows (E3 2026-08-29) ──────────────────────────────────────────────
 * E3: *"Instead of arrows up and down, we just do the drag and drop."* And on
 * the tab strip: *"the arrows next to the tabs within the module make no sense
 * because there's no up or down in that."* A rail IS vertical, so ↑/↓ at least
 * described the axis there — but two verbs for one gesture is one too many, and
 * a button per module is the clutter he asked to remove.
 *
 * ── Permanent modules get NOTHING, not a disabled control ──────────────────
 * E3: *"we never allow the executive module to be deleted. That's the whole
 * point of an entire dashboard."* The state layer already refuses it — but the
 * control was still rendered, so clicking × on Executive did nothing and the UI
 * looked broken while the rule was working. A control a person can see but not
 * use is an invitation to ask why. So a permanent module is not draggable and
 * carries no handle: the affordance tells the truth about the rule.
 */
export function DraggableModule({
  moduleKey,
  permanent,
  label,
  children,
}: {
  moduleKey: string;
  permanent: boolean;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  const payload: DragPayload = { from: 'module', moduleKey };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `module:${moduleKey}`,
    data: payload,
    disabled: permanent,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `module-slot:${moduleKey}`,
    data: payload,
  });

  if (permanent) return <>{children}</>;

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        setDropRef(node);
      }}
      className={`kd-dragmod ${isDragging ? 'dragging' : ''} ${isOver ? 'over' : ''}`.trim()}
      aria-label={`${label} — drag to reorder, or out to remove`}
      {...listeners}
      {...attributes}
    >
      {children}
    </div>
  );
}

/**
 * The package as a drop target — dropping here REMOVES.
 *
 * The palette doubles as the trash, which is the detail that makes Apple's
 * model coherent: there is one place elements live, and putting something back
 * there IS removing it from the toolbar. A separate bin would imply a second
 * destination and a way to lose things.
 */
export function PackageDropZone({ children }: { children: React.ReactNode }): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: 'package' });
  return (
    <div ref={setNodeRef} className={isOver ? 'kd-pkgzone over' : 'kd-pkgzone'}>
      {children}
    </div>
  );
}
